import React from 'react'
import { useDispatch } from 'react-redux'
import { List, ListItem } from '@mobi/component-library/Base/List'
import { addSearchTermToPrevious } from '@core/Areas/Search/Store'
import {
  isSearchResultAcceptor,
  isSearchResultFobEvent,
  isSearchResultFobProposition,
  isSearchResultRace,
} from '@core/Areas/Search/helpers/typeGuards'
import type { SearchResult } from '@core/Areas/Search/types'
import { OverlayClose } from '@core/Components/Overlay'
import { appHistory } from '@core/Utils/routing/history'
import { trackSearch, trackSearchInteraction } from '../../helpers/analytics'
import { EmptyScreen } from '../Common/EmptyScreen'
import { LoadingSkeleton } from '../Common/LoadingSkeleton'
import { NoSearchResults } from '../Common/NoSearchResults'
import { SearchErrored } from '../Common/SearchErrored'
import { RacingSearchResult } from './Components/RacingSearchResult'
import { getRedirectUrl } from './helpers/getRedirectUrl'

export const RacingSearch: React.FC<{
  isError: boolean
  isLoading: boolean
  searchTerm: string
  handleImmediateSearch: (searchTerm: string) => void
  searchWasImmediate: boolean
  searchResults?: SearchResult[]
}> = ({
  isError,
  isLoading,
  searchResults,
  searchTerm,
  handleImmediateSearch,
  searchWasImmediate,
}) => {
  const dispatch = useDispatch()

  const searchResultsToRender = searchResults?.filter(filterForKnownSearchResult)
  const hasNoResults = searchResultsToRender?.length === 0

  React.useEffect(() => {
    if (isError || isLoading || !searchResultsToRender) {
      return
    }
    trackSearch({
      interaction_type: 'Search Results Viewed',
      search_term: searchTerm.toLowerCase(),
      resultsFound: searchResultsToRender.length > 0,
      searchOrigin: searchWasImmediate ? 'pre-filled' : 'manual',
    })
  }, [isError, isLoading, searchResultsToRender, searchTerm, searchWasImmediate])

  if (isError) {
    return <SearchErrored />
  }

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (!searchResultsToRender || (hasNoResults && searchTerm.length < 4)) {
    return <EmptyScreen searchMode='racing' handleImmediateSearch={handleImmediateSearch} />
  }

  if (hasNoResults) {
    return <NoSearchResults />
  }

  const handleResultClick = (searchResult: SearchResult) => {
    dispatch(addSearchTermToPrevious({ type: 'racing', searchTerm }))
    appHistory.push(getRedirectUrl(searchResult))
    OverlayClose()

    trackSearchInteraction({
      interaction_type: 'Search Result Clicked',
      search_term: searchTerm.toLowerCase(),
      resultType: searchResult.kind,
      ...getEventDetails(searchResult),
    })
  }

  return (
    <List variant='card' itemSize='dynamic'>
      {searchResultsToRender.map(searchResult => (
        <ListItem
          key={searchResult.id}
          onClick={() => handleResultClick(searchResult)}
          data-tid-search-result={`${searchResult.kind.toLowerCase()}-${searchResult.id}`}
          content={
            <RacingSearchResult
              searchResult={searchResult}
              highlights={searchResult.metadata.highlights}
            />
          }
        />
      ))}
    </List>
  )
}

// =============
// Local Helpers
// =============

function filterForKnownSearchResult(searchResult: SearchResult): boolean {
  return (
    isSearchResultAcceptor(searchResult) ||
    isSearchResultRace(searchResult) ||
    isSearchResultFobEvent(searchResult) ||
    isSearchResultFobProposition(searchResult)
  )
}

function getEventDetails(searchResult: SearchResult) {
  const fob = isSearchResultFobProposition(searchResult) ? searchResult : null
  const fobEe = isSearchResultFobEvent(searchResult) ? searchResult : null
  const fobEvent = fob?.fobProposition.fobEvent || fobEe?.fobEvent
  return {
    raceCode: fobEvent?.contestType,
    raceStatus: fobEvent?.isBettable
      ? 'Open'
      : fobEvent?.isSettled
        ? 'Closed'
        : fobEvent?.isAbandoned
          ? 'Abandoned'
          : fobEvent?.isSuspended
            ? 'Suspended'
            : 'Closed',
  }
}
